import { useLocalization } from "@fluent/react";
import { Button, TextInput } from "@narmi/design_system";
import { useVisitorData } from "@fingerprintjs/fingerprintjs-pro-react";
import {
  ContextForm,
  PasswordTextInput,
  useFormData,
  useNotificationContext,
  CsrfTokenMiddleware,
  jsonFromDocument,
} from "cerulean";
import type { InstitutionFeatures } from "byzantine/src/types";
import React from "react";

import ReCAPTCHAFormField from "../form/ReCAPTCHAFormField";
import { isFingerprintEnabled } from "../fingerprint/OptionalFingerprintProvider";
import type Institution from "../../types/institution";
import { createHash } from "./utils";
import { maskUsername } from "../../src/utils/maskUsername";
import "./authentication.css";

type LoginPageContainerProps = {
  deviceEnrolled?: boolean;
  institutionShortName?: string;
  institution: Institution;
  recaptchaPublicKey?: string;
  features: InstitutionFeatures;
};

const deviceMonitoringModes = ["report", "decision"];

const LoginPageContainer = ({
  deviceEnrolled,
  institutionShortName,
  institution,
  recaptchaPublicKey,
  features,
}: LoginPageContainerProps) => {
  const { sendNotification } = useNotificationContext();
  const { formData, onChange } = useFormData({});
  const { l10n } = useLocalization();
  const linkProfileUrl = window.location.pathname.match("/login/link");
  const deviceIdInput = React.useRef<HTMLInputElement>(null);
  const deviceRequestIdInput = React.useRef<HTMLInputElement>(null);
  const usernameHash = React.useCallback(
    async (message: string) => createHash(message),
    []
  );

  const { getData } = useVisitorData(
    {
      extendedResult: true,
      tag: { userAction: "login" },
    },
    { immediate: false }
  );

  const showCaptcha = JSON.parse(
    document.getElementById("show_captcha")?.textContent || "false"
  ) as boolean;
  const authUsername = JSON.parse(
    document.getElementById("auth_username")?.textContent || "false"
  ) as boolean;

  const linkedProfileUsername = JSON.parse(
    document.getElementById("new_username")?.textContent || "null"
  ) as string | null;

  const form = React.useRef<HTMLFormElement>(null);

  React.useEffect(() => {
    const formErrors = jsonFromDocument("form_non_field_errors", "[]");
    if (formErrors.length) {
      sendNotification({ type: "negative", text: formErrors[0] });
    }
    let username = jsonFromDocument("username", "");
    if (linkedProfileUsername !== null) {
      username = maskUsername(linkedProfileUsername);
    }

    if (username) {
      onChange({ username });
    }
  }, [linkedProfileUsername, onChange, sendNotification]);

  const onBlur = () => {
    if (formData.username) {
      const username = formData.username.trim();
      onChange({ username });
    }
  };

  const institutionUserType =
    institution.type === "bank"
      ? l10n.getString("user-type-bank", null, "customer")
      : l10n.getString("user-type-credit-union", null, "member");

  const header = () => {
    if (deviceEnrolled && !linkProfileUrl) {
      return l10n.getString("heading-welcome-back");
    }
    if (linkedProfileUsername) {
      return l10n.getString("sign-in");
    }
    return l10n.getString("heading-welcome");
  };

  const subHeader = () => {
    if (linkedProfileUsername) {
      return l10n.getString("enter-password");
    }
    return l10n.getString(
      "enter-profile-credentials",
      null,
      "Enter the credentials for the profile you would like to link."
    );
  };
  const loginAction = `${window.location.pathname}${window.location.search}`;
  const shouldCollectFingerprint = isFingerprintEnabled({
    fingerprintApiKey: institution.fingerprintApiKey,
    deviceMonitoringEnabled: deviceMonitoringModes.includes(
      features.third_party_login_evaluations ?? "off"
    ),
  });

  return (
    <div className="authentication-container">
      <div className="mobile-web-extra-space">
        <h1>{header()}</h1>
        {linkProfileUrl && <div className="margin--top--xs">{subHeader()}</div>}
        {!deviceEnrolled && (
          <div>
            {l10n.getString("helptext-get-started", {
              institutionName: institutionShortName || "",
              userType: institutionUserType,
            })}
          </div>
        )}
      </div>
      <ContextForm
        ref={form}
        data={formData}
        onChange={onChange}
        action={loginAction}
        autoComplete="off"
        method="post"
      >
        <span className={linkedProfileUsername ? "disabled-text-box" : ""}>
          {linkedProfileUsername && (
            <input
              type="hidden"
              name="username"
              value={linkedProfileUsername}
            />
          )}
          <ContextForm.Field required>
            <TextInput
              name={linkedProfileUsername ? "username_display" : "username"}
              field="username"
              label={
                authUsername
                  ? l10n.getString("label-username", null, "Username")
                  : l10n.getString("label-email", null, "Email")
              }
              readOnly={Boolean(linkedProfileUsername)}
              onBlur={onBlur}
            />
          </ContextForm.Field>
        </span>

        <ContextForm.Field required>
          <PasswordTextInput field="password" />
        </ContextForm.Field>
        <ReCAPTCHAFormField
          showCaptcha={showCaptcha}
          recaptchaPublicKey={recaptchaPublicKey}
        />
        {shouldCollectFingerprint && (
          <ContextForm.Field>
            <input
              type="hidden"
              id="device_id"
              name="device_id"
              ref={deviceIdInput}
            />
          </ContextForm.Field>
        )}
        {shouldCollectFingerprint && (
          <ContextForm.Field>
            <input
              type="hidden"
              id="device_request_id"
              name="device_request_id"
              ref={deviceRequestIdInput}
            />
          </ContextForm.Field>
        )}

        <CsrfTokenMiddleware />
        <ContextForm.ActionBar>
          <ContextForm.Action
            onSubmit={(_data: unknown, e: React.MouseEvent<HTMLElement>) => {
              e.preventDefault();
              if (shouldCollectFingerprint) {
                usernameHash(
                  `${institution.internalName}-${
                    linkedProfileUsername || formData.username
                  }`
                ).then((hashedUsername) => {
                  getData({ ignoreCache: true, linkedId: hashedUsername })
                    .then((data) => {
                      if (deviceIdInput.current) {
                        deviceIdInput.current.value = data.visitorId;
                      }
                      if (deviceRequestIdInput.current) {
                        deviceRequestIdInput.current.value = data.requestId;
                      }
                      form.current?.submit();
                    })
                    .catch((error) => {
                      console.warn("Fingerprint collection failed:", error);
                      form.current?.submit();
                    });
                });
              } else {
                form.current?.submit();
              }
            }}
          >
            <Button
              as="button"
              label={l10n.getString("button-login", null, "Log in")}
              testId="login"
            />
          </ContextForm.Action>
          <ContextForm.Action
            onSubmit={() => {
              window.location.assign("/password_reset");
            }}
            noValidation
          >
            <Button
              kind="plain"
              type="button"
              data-test-element="button-forgot-password"
              testId="forgot-password"
              label={l10n.getString(
                "button-forgot-credentials",
                null,
                "Forgot your username or password?"
              )}
            />
          </ContextForm.Action>
        </ContextForm.ActionBar>
      </ContextForm>
    </div>
  );
};

export default LoginPageContainer;
